// TODO: import { localStorage } from '../utils/namespaced-storage';
import { QueryClient } from '@tanstack/react-query';

import createDebug from '../utils/debug';
import { shareRealtimeConnection } from './realtime-connection';
import getGlobalValue from '../utils/get-global-value';
import createQueryConfig from '../queries';
import {
  UnauthorizedError,
  UnconfirmedEmailError,
  WrappedError,
} from '../utils/custom-errors';
import { fetch, window } from '../env/browser';
import { fetchJson } from '../utils/fetch';
import {
  API_BASE_URL,
  LEGACY_API_BASE_URL,
  COMPANY_BASE_URL,
  GRAPHQL_BASE_URL,
  REALTIME_APP_KEY,
  REALTIME_HOST,
  WHITE_LABEL,
  AUTH_BASE_URL,
  CELLO_PRODUCT_ID,
} from '../constants';
import {
  CURRENT_MERCHANT_V2_FIELDS,
  CURRENT_MERCHANT_V2_INCLUDE,
} from '../shared/constants/merchant';
import type {
  AppShellApiClient,
  CurrentMerchantData,
  LocationsResponse,
  MyAccountData,
  SessionPermissionsPayload,
} from '../shared/types';
import type {
  IWhiteLabelConfiguration,
  IBookingConfigResponse,
} from '@shore/types';
import {
  clearPreferredMerchantId,
  clearSsoToken,
  getPreferredMerchantId,
  persistSsoToken,
  resolveSsoAuth,
  setPreferredMerchantId,
} from './sso';

const debug = createDebug('initializers:api-client');

type CreateShoreClient = (config: Record<string, unknown>) => AppShellApiClient;

export interface CreateApiClientParams {
  merchantId?: string;
  merchantAccountId?: string;
  organizationId?: string;
  accessToken?: string;
  createShoreClient?: CreateShoreClient;
}

export type ApiClientCreator = (
  params?: CreateApiClientParams
) => AppShellApiClient;

// Errors thrown by the auth token query carry the HTTP response details.
interface AuthTokenError {
  status?: number;
  responseBody?: { errors?: { detail?: string } };
}

const switchLocationOnCore = (id: string): Promise<Response> =>
  fetch(
    `${COMPANY_BASE_URL}/merchant/api/locations?merchant_profile_id=${id}`,
    { credentials: 'include' }
  );

function fetchLatestSubscription(
  merchantId: string,
  token: string
): Promise<Response> {
  const url = `${API_BASE_URL}/v3/subscription/merchants/${merchantId}/latest_subscription`;

  return fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

/**
 * Monthly appointment allowance and spend for a merchant.
 *
 * Served by subscription_service, alongside `fetchLatestSubscription` above.
 * Answers 404 for merchants that have no allowance.
 */
function fetchAppointmentUsage(
  merchantId: string,
  token: string
): Promise<Response> {
  const url = `${API_BASE_URL}/v3/subscription/merchants/${merchantId}/usage`;

  return fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

function fetchPortalSession(
  merchantId: string,
  token: string
): Promise<Response> {
  const url = `${API_BASE_URL}/v3/subscription/portal_sessions`;

  return fetch(url, {
    method: 'POST',
    body: JSON.stringify({ merchant_id: merchantId }),
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
}

function setupDownPayments(
  merchantId: string,
  params: { percentage: number; type?: string },
  token: string
): Promise<Response> {
  const url = `${API_BASE_URL}/v1/shorepay/down_payment/${merchantId}/enable`;

  const body: {
    down_payment_percentage: number;
    down_payment_type?: string;
  } = { down_payment_percentage: params.percentage };
  if (params.type) {
    body.down_payment_type = params.type;
  }

  return fetch(url, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/vnd.api+json',
    },
  });
}

function disableDownPayments(
  merchantId: string,
  token: string
): Promise<Response> {
  const url = `${API_BASE_URL}/v1/shorepay/down_payment/${merchantId}/disable`;

  return fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/vnd.api+json',
    },
  });
}

function createCollectInvoicesPage({
  merchantId,
  token,
}: {
  merchantId: string;
  token: string;
}): Promise<Response> {
  const url = `${API_BASE_URL}/v3/subscription/merchants/${merchantId}/collect_now`;

  return fetch(url, {
    method: 'POST',
    body: JSON.stringify({ product: 'calendar' }),
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
}

function createCheckoutSession(
  {
    merchantId,
    productFamily,
    redirectUrl,
    locale,
    metadata,
    operation,
  }: {
    merchantId: string;
    productFamily: string;
    redirectUrl: string;
    locale: string;
    metadata?: Record<string, unknown>;
    operation?: string;
  },
  token: string
): Promise<Response> {
  const url = `${API_BASE_URL}/v3/subscription/checkout_sessions`;

  return fetch(url, {
    method: 'POST',
    body: JSON.stringify({
      merchant_id: merchantId,
      product_family: productFamily,
      redirect_url: redirectUrl,
      metadata,
      locale,
      ...(operation ? { operation } : {}),
    }),
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
}

export const getCurrentLocationFromCore = (): Promise<string> =>
  fetchJson<LocationsResponse>(`${COMPANY_BASE_URL}/merchant/api/locations`, {
    credentials: 'include',
  }).then(({ json: data }) => data.current_location_id);

export const getLocationsFromCore = (): Promise<LocationsResponse> =>
  fetchJson<LocationsResponse>(`${COMPANY_BASE_URL}/merchant/api/locations`, {
    credentials: 'include',
  }).then(({ json: data }) => data);

export function createApiClient({
  merchantId = '00000000-0000-0000-0000-000000000000',
  merchantAccountId = '00000000-0000-0000-0000-000000000000',
  organizationId = '00000000-0000-0000-0000-000000000000',
  accessToken,
  // NOTE: dependency injection only for testing, intentionally getting
  //       Shore JS Client from window here, because it's coming from
  //       Shore CDN and we don't want to manage the npm package
  createShoreClient = getGlobalValue(
    'Shore.Client.create'
  ) as CreateShoreClient,
}: CreateApiClientParams = {}): AppShellApiClient {
  const apiBaseUrl = API_BASE_URL;
  const companyBaseUrl = COMPANY_BASE_URL;
  const config = {
    // Legacy Endpint
    availabilityBaseUrl: LEGACY_API_BASE_URL,
    // TODO: remove these when SJC was refactored
    apiV1BaseUrl: `${apiBaseUrl}/v1`,
    apiV2BaseUrl: `${apiBaseUrl}/v2`,
    appointmentBaseUrl: `${apiBaseUrl}/v1`,
    appointmentV2BaseUrl: `${apiBaseUrl}/v2`,
    // auth needs to use the base url for cookie reasons
    authBaseUrl: `${companyBaseUrl}/merchant/api/auth`,
    communicationBaseUrl: `${apiBaseUrl}/v1`,
    customerBaseUrl: `${apiBaseUrl}/v1`,
    customerMerchantBaseUrl: `${companyBaseUrl}/merchant/api/customers`,
    customerMerchantV2BaseUrl: `${companyBaseUrl}/merchant/api/v2/customers`,
    customersV2BaseUrl: `${apiBaseUrl}/v2`,
    employeeBaseUrl: `${apiBaseUrl}/v1`,
    employeeV2BaseUrl: `${apiBaseUrl}/v2`,
    filestorageBaseUrl: `${apiBaseUrl}/v1`,
    merchantAccountBaseUrl: `${apiBaseUrl}/v1`,
    merchantAccountV2BaseUrl: `${apiBaseUrl}/v2`,
    merchantBaseUrl: `${apiBaseUrl}/v1`,
    merchantV2BaseUrl: `${apiBaseUrl}/v2`,
    messagingBaseUrl: `${apiBaseUrl}/v1`,
    newsletterBaseUrl: `${apiBaseUrl}/v1`,
    notificationsBaseUrl: `${apiBaseUrl}/v1`,
    paymentsBaseUrl: `${companyBaseUrl}/merchant/api/charges`,
    reminderBaseUrl: `${apiBaseUrl}`,
    resourceBaseUrl: `${apiBaseUrl}/v1`,
    resourcesV2BaseUrl: `${apiBaseUrl}/v2`,
    seriesV2BaseUrl: `${apiBaseUrl}/v2`,
    serviceBaseUrl: `${apiBaseUrl}/v1`,
    appointmentGroupBaseUrl: `${apiBaseUrl}/v1`,

    realtime: {
      appKey: REALTIME_APP_KEY,
      authEndpoint: `${apiBaseUrl}/v1/pusher/auth`,
      httpHost: REALTIME_HOST,
      wsHost: REALTIME_HOST,
      encrypted: true,
      disableStats: true,
    },
    organizationId,
    merchantId,
    merchantAccountId,
    apiBaseUrl: API_BASE_URL,
    graphqlBaseUrl: GRAPHQL_BASE_URL,
    authDomain: AUTH_BASE_URL,
    accessToken,
  };

  debug('using config', config);

  return createShoreClient(config);
}

export default (async function configureApiClient(
  apiClientCreator: ApiClientCreator = createApiClient,
  queryClient: QueryClient = new QueryClient()
) {
  let accountData: MyAccountData;
  let merchantData: CurrentMerchantData | undefined;
  let sessionPermissions: SessionPermissionsPayload | undefined;
  let bookingConfigs: IBookingConfigResponse | undefined;
  const sso = await resolveSsoAuth();
  const tmpApiClient = apiClientCreator({ accessToken: sso.accessToken });
  const queryConfig = createQueryConfig(queryClient, tmpApiClient);

  try {
    await queryClient.fetchQuery(queryConfig.authToken());
    if (sso.ssoMode && sso.accessToken) {
      persistSsoToken(sso.accessToken);
    }
  } catch (error) {
    const authError = error as AuthTokenError | null;
    if (authError && authError?.status === 401) {
      if (sso.ssoMode) {
        clearSsoToken();
      }
      if (
        authError?.responseBody?.errors?.detail === 'email_confirmation_missing'
      ) {
        throw new UnconfirmedEmailError(
          'Please ensure your email is confirmed',
          error as Error
        );
      }
      throw new UnauthorizedError(
        'Please ensure you are logged in!',
        error as Error
      );
    }

    throw new WrappedError('Unable to authorize', error as Error);
  }

  try {
    // @shore/types' IMerchantAccountResponse.employee/organization are typed
    // nullable (they're only populated when requested) — the query requests
    // include: ['employee', 'organization'], so they're always present here.
    accountData = (await queryClient.fetchQuery(
      queryConfig.merchantAccountV2()
    )) as MyAccountData;
  } catch (error) {
    throw new WrappedError('Requesting account infos failed', error as Error);
  }

  // SSO mode: skip cookie-only Core locations.
  // Order: fresh exchange merchant_id (partner link intent, only set on
  // new sso_code trade) → first-party preferred id (in-session Switch) → findAll.
  // Cookie path unchanged when ssoMode is false.
  const currentMerchantId = sso.ssoMode
    ? (sso.merchantId ?? getPreferredMerchantId(accountData.id))
    : await getCurrentLocationFromCore();
  // try to fetch merchant data of currentMerchantId if defined
  if (currentMerchantId) {
    try {
      merchantData = (await queryClient.fetchQuery(
        queryConfig.merchantV2(currentMerchantId)
      )) as CurrentMerchantData;
      // SSO only: sticky-store the merchant we actually loaded (exchange or preferred).
      if (sso.ssoMode) {
        setPreferredMerchantId(accountData.id, merchantData.id);
      }
    } catch (error) {
      debug(error);
      // Drop sticky preferred so later SSO reloads don't keep retrying a bad id.
      if (sso.ssoMode) {
        clearPreferredMerchantId(accountData.id);
      }
    }
  }

  // First-merchant fallback (1st visit / SSO without merchant_id / broken preferred id).
  // MUST be v2 findAll — legacy merchant.findMerchants hits /v1/merchants with array-shaped
  // fields and 500s on Core. devour + merchantV2 encodes sparse fields correctly.
  if (!merchantData) {
    try {
      const listed = await tmpApiClient.merchantV2.findAll({
        page: { size: 1 },
        sort: 'created_at', // asc = oldest first
        fields: CURRENT_MERCHANT_V2_FIELDS,
        include: [...CURRENT_MERCHANT_V2_INCLUDE],
      });
      merchantData = (
        Array.isArray(listed) ? listed[0] : listed
      ) as CurrentMerchantData;
    } catch (error) {
      throw new WrappedError('Requesting merchants failed', error as Error);
    }
  }

  if (sso.ssoMode && !currentMerchantId) {
    debug(
      'SSO boot without exchange/preferred merchant id; defaulting to first location'
    );
  }

  try {
    const { cello_jwt, given_name, surname, email } = accountData.employee;
    const { locale } = accountData;
    const localeFormattedForCello =
      (locale && locale.split && locale.split('-')[0]) || 'en';

    if (CELLO_PRODUCT_ID) {
      window.cello = window.cello || { cmd: [] };

      window.cello.cmd.push(function (cello) {
        cello.boot({
          productId: CELLO_PRODUCT_ID,
          token: cello_jwt,
          hideDefaultLauncher: true,
          customLauncherSelector: '.cello-launcher',
          language: localeFormattedForCello,
          productUserDetails: {
            firstName: given_name ?? undefined,
            lastName: surname,
            email,
          },
          announcement: {
            selector: '.cello-launcher-announcement',
            position: 'bottom',
            positionOffset: {
              x: '0px',
              y: '0px',
            },
          },
        });
      });
    }
  } catch (error) {
    throw new WrappedError('Unable to boot Cello', error as Error);
  }

  // create final API client
  // merchantData is the merchant we actually loaded (preferred id or first-merchant
  // fallback). Apps/booking must use this same id — not currentMerchantId, which
  // can still be set after a failed merchantV2 fetch.
  const merchantAccountId = accountData.id;
  const merchantId = merchantData.id;
  const organizationId = merchantData.organization.id;
  const finalApiClient = apiClientCreator({
    merchantAccountId,
    merchantId,
    organizationId,
    accessToken: sso.accessToken,
  });

  // convenience method for loading the avatar image URL
  const getAvatarUrl = async (): Promise<string | null> => {
    try {
      const data = await finalApiClient.employeeV2.find(
        accountData.employee.id
      );
      return data.image_url;
    } catch (error) {
      /* istanbul ignore next */
      debug(error);
      return null; // just return 'null' if there's no image available
    }
  };

  const getWhiteLabelConfig = async (): Promise<
    Partial<IWhiteLabelConfiguration>
  > => {
    try {
      const { json } = await fetchJson<IWhiteLabelConfiguration>(WHITE_LABEL);
      return json;
    } catch (error) {
      debug(error);
      return {};
    }
  };

  if (merchantId) {
    try {
      sessionPermissions = await queryClient.fetchQuery(
        queryConfig.merchantAppsV2(merchantId)
      );
    } catch (error) {
      throw new WrappedError(
        'Requesting merchant user app permissions failed',
        error as Error
      );
    }
  }

  if (merchantId) {
    try {
      bookingConfigs = await queryClient.fetchQuery(
        queryConfig.bookingConfigsV2.find({ merchantId })
      );
    } catch (error) {
      throw new WrappedError(
        'Requesting merchant booking configs failed',
        error as Error
      );
    }
  }

  return {
    ...finalApiClient,

    // One connection for the whole session: the shell subscribes twice of its
    // own accord and hands this client to every micro app, and the shore
    // client's own connect is not safe to call concurrently — see
    // initializers/realtime-connection.ts.
    realtime: shareRealtimeConnection(finalApiClient.realtime),

    /* custom convenience methods */
    getAvatarUrl,
    getCurrentMerchantData: () => Promise.resolve(merchantData),
    getMyAccountData: () => Promise.resolve(accountData),
    getSessionPermissions: () => Promise.resolve(sessionPermissions),
    getBookingConfigs: () => Promise.resolve(bookingConfigs),
    getLocations: getLocationsFromCore,
    subscriptionV3: {
      fetchLatestSubscription,
      fetchPortalSession,
      createCheckoutSession,
      createCollectInvoicesPage,
      fetchAppointmentUsage,
    },
    downPayments: {
      setupDownPayments,
      disableDownPayments,
    },
    getWhiteLabelConfig,
    switchLocationOnCore,
  };
});
